import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { addWindow, resetWindows, setMobileView } from 'store/slice/pdpStateSlice';
import { IGmcOffers } from 'shared/types/Offer';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { LOCALES } from 'localization/constants/configEnums';
import { OffersProvider } from 'productTemplate/providers/OffersProvider';
import { sendTrackingPdpView } from 'productTemplate/utils/sendPdpViewTrackingEvent';
import { SizeConversionProvider } from 'productTemplate/providers/SizeConversionProvider';
import { useFetchBuyBarVariantsQuery } from 'store/api/buyBarVariantsApi';
import { useFetchProductVariantQuery } from 'store/api/productVariantApi';
import { useLocation } from 'shared/hooks/useLocation';
import { useMParticleContext } from 'tracking/hooks/useMParticleContext';
import { useStoreDispatch } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const ProductDetailPageDesktop = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageDesktop').then(
      (mod) => mod.ProductDetailPageDesktop,
    ),
  { ssr: true },
);
const ProductDetailPageMobile = dynamic(
  () =>
    import('productTemplate/components/ProductDetailPageMobileDeprecated').then(
      (mod) => mod.ProductDetailPageMobileDeprecated,
    ),
  { ssr: true },
);

const ProductTemplateMobileSearchLayout = dynamic(
  () =>
    import('productTemplate/components/ProductTemplateMobileSearchLayout').then(
      (mod) => mod.ProductTemplateMobileSearchLayout,
    ),
  { ssr: true },
);

interface IProductDetailPage {
  isCrawler: boolean;
  locale?: LOCALES;
  offers: IGmcOffers;
  productTemplate: IProductTemplate;
}

const ProductDetailPageLanding = ({
  isCrawler = false,
  offers,
  productTemplate,
  locale,
}: IProductDetailPage) => {
  const { query, asPath, events, pathname } = useRouter();
  const storeDispatch = useStoreDispatch();
  const { isMParticleReady } = useMParticleContext();
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const {
    brandName,
    id,
    isAvailabilityRestricted,
    name: productName,
    slug,
  } = productTemplate || {};

  const [variantIdFromUrl, setVariantIdFromUrl] = useState<string>(null);
  const { data: productVariantData } = useFetchProductVariantQuery(
    { id: variantIdFromUrl },
    { skip: !variantIdFromUrl },
  );
  const { selectedCountryCode } = useLocation();
  const isItemAvailabilityRestricted = isAvailabilityRestricted;
  const { isFetching } = useFetchBuyBarVariantsQuery(
    {
      id,
      selectedCountryCode,
    },
    { skip: isItemAvailabilityRestricted },
  );

  const prevPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('prevPath') : null;
  const currentPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('currentPath') : null;

  useEffect(() => {
    const hash = /#id=((\w|-)+)/.exec(asPath);
    if (query && query.productId) {
      setVariantIdFromUrl(query.productId as string);
    } else if (hash && hash[1]) {
      setVariantIdFromUrl(hash[1]);
    }
  }, [query, asPath]);

  // Show variant window with a delay to display on top of facts window.
  // 60ms delay because the facts window shows up with a 50ms delay.
  useEffect(() => {
    let timer;
    if (productVariantData) {
      timer = setTimeout(() => {
        storeDispatch(addWindow(productVariantData));
        storeDispatch(setMobileView({ view: 'detail', size: productVariantData.size }));
      }, 60);
    }
    return () => clearTimeout(timer);
  }, [productVariantData]);

  useEffect(() => {
    // on initial page load the prevPath is still the currentPath so wait until currentPath is asPath
    if (currentPath === asPath && isMParticleReady) {
      sendTrackingPdpView({ prevPath, id, name: productName, slug, brandName, query });
    }
  }, [currentPath, prevPath, asPath, isMParticleReady, id, query, pathname]);

  const handleBeforeUnload = () => {
    storeDispatch(resetWindows());
  };

  useEffect(() => {
    if (!isMobile) {
      events.on('routeChangeStart', handleBeforeUnload);
    }

    return () => {
      events.off('routeChangeStart', handleBeforeUnload);
    };
  }, [isMobile]);

  return (
    <>
      {isCrawler && (
        <ProductTemplateMobileSearchLayout
          offersData={offers}
          locale={locale}
          isAvailabilityRestricted={isItemAvailabilityRestricted}
        />
      )}
      {!isCrawler && (
        <SizeConversionProvider>
          <OffersProvider>
            {isMobile && (
              <ProductDetailPageMobile
                isAvailabilityRestricted={isItemAvailabilityRestricted}
                isFetchingBuyBarData={isFetching}
              />
            )}
            {(isTablet || isDesktop) && (
              <ProductDetailPageDesktop
                isAvailabilityRestricted={isItemAvailabilityRestricted}
                isFetchingBuyBarData={isFetching}
              />
            )}
          </OffersProvider>
        </SizeConversionProvider>
      )}
    </>
  );
};

export default ProductDetailPageLanding;
